import APIServicesRegistry from '@lib/api-services/APIServicesRegistry';
import ProjectApiService from '@lib/api-services/ProjectApiService';
import buildResourcePath from '@lib/buildResourcePath';
import UIServicesRegistry from '@lib/ui-services/UIServicesRegistry';
import XMessageUIService from '@lib/ui-services/XMessageUIService';
import { observer } from 'mobx-react';
import React, { Component } from 'react';
import ProjectStore from '../../../store/projectStore';
import Button from '../../common/Button';
import FilesCard from '../../file/FilesCard';
import IconSave from '../../icons/IconSave';
import ComunicationCard from './../../project/CommunicationCard';
import HistoryCard from './../../project/HistoryCard';
import ProjectCollaborationCard from './../../project/ProjectCollaborationCard';
import ProjectCustomer from './../../project/ProjectCustomer';
import ProjectDeleteModal from './../../project/ProjectDeleteModal';
import Loader from '../../common/Loader';
import TWSProjectGeneralInformation from "@lib/components/tws-project/edit/TWSProjectGeneralInformation";
import TWSProjectAPIService from "@lib/api-services/TWSProjectApiService";
import TWSProjectCalculations from "@lib/components/tws-project/edit/TWSProjectCalculations";
import MessageAPIService from '@lib/api-services/MessageAPIService';
import { MessagesAPIService } from '@lib/api-services/MessagesAPIService';
import GenerateDocumentationsModal from '../calculation/calculation-printouts/GenerateDocumentationsModal'
import { Message } from "../../../../src/application/Contracts";

interface SelfProps {
    model: any;
}

const TWSProjectEditPage = observer(
    class TWSProjectEditPage extends Component<SelfProps> {

        state = {
            calculationsDocumentationModalIsOpen: false,
            _forceUpdate: false
        }

        projectApiService: any;
        xMessageUIService: any;

        constructor(props: any) {
            super(props)
            ProjectStore.initModel({
                id: props.model.id,
                isEditable: props.model.isEditable,
                generalInformation: props.model.generalInformation,
                files: props.model.files,
                calculations: props.model.calculations,
                viewers: props.model.viewers,
                participants: props.model.participants,
                summary: props.model.summary,
                _forceUpdate: false
            })

            window.scheduleAtDocumentReady(() => {
                this.projectApiService = APIServicesRegistry.getService(TWSProjectAPIService)
                this.xMessageUIService = UIServicesRegistry.getService(XMessageUIService)
                this.setState({ _forceUpdate: !this.state._forceUpdate });
            })


            window.onbeforeunload = e => {
                e = e || window.event;

                if (ProjectStore.dirty) {
                    const message = __('Changes that you have made will not be saved.');

                    if (e) {
                        e.returnValue = message;
                    }

                    return message;
                }
            }
        }

        onSaveClick = () => {
            if (!ProjectStore.canSave())
                return;

            ProjectStore.setDuringSave(true);

            let model = ProjectStore.getTransformedFormModel();
            ProjectStore.setDirty(false);
            if (ProjectStore.isNew())
                this.projectApiService.create(model).then(() => {

                    ProjectStore.setDuringSave(false);
                })
            else
                this.projectApiService.edit(model).then((response: any) => {
                    ProjectStore.setDuringSave(false);
                    ProjectStore.updateCalculationIds(response.data.calculations)
                });
        }

        onDeleteClick = () => {
            if (!ProjectStore.isNew())
                ProjectStore.setDeleteModalVisible(true)
        }

        onCopyClick = () => {
            this.copy();
        }

        onDeletePostClick = () => {
            ProjectStore.duringDelete = true
            this.projectApiService.delete(ProjectStore.formModel.id.value).then(() => {
                window.location.href = buildResourcePath('TWSProjects')
            })
        }

        getTitle = () => {
            if (ProjectStore.isNew())
                return (
                    <div className="project-edit-title">
                        {__('New Project')}
                    </div>
                );

            return (
                <div className="project-edit-title">
                    {this.props.model.title}
                </div>
            )
        }

        onProjectFilesChange = (files: any) => {
            ProjectStore.formModel.files = files;
            ProjectStore.setDirty(true);
        }

        onCollaborationParticipantsChanged = (participants: any) => ProjectStore.formModel.participants = participants;

        onCollaborationViewersChanged = (viewers: any) => ProjectStore.formModel.viewers = viewers;

        getActiveUser() {
            let usersList = this.props.model.projectsMembers.members;
            let activeUserId = this.props.model.projectsMembers.activeUserId;
            let user = usersList.filter((x: any) => x.value === activeUserId && x.memberType === 0)[0];
            return {
                user: user,
                isAdministrator: true
            }
        }

        getProjectMembers() {
            return {
                viewers: this.props.model.viewers.value,
                participants: this.props.model.participants.value
            }
        }

        copy = () => {
            ProjectStore.duringCopy = true;
            this.projectApiService.copyAndEdit(ProjectStore.formModel.id.value)
        }

        renderDocumentationsDropdown(): React.ReactNode {
            return (
                <React.Fragment>
                    <GenerateDocumentationsModal
                        id={this.props.model.id.value}
                        isOpen={this.state.calculationsDocumentationModalIsOpen}
                        onClose={() => {
                            this.setState({ calculationsDocumentationModalIsOpen: false });
                        }}
                    />
                    {this.props.model.calculations?.items?.length > 0 && (
                        <span className="desktop-only">
                            <Button className="nowrap" onClick={() => this.setState({ calculationsDocumentationModalIsOpen: true })}>
                                {__("Generate documentation")}
                            </Button>
                        </span>
                    )}
                </React.Fragment>
            );
        }

        getIsViewer() {
            let permissions = this.props.model.permission.value;
            return !permissions.canAddCalculation && !permissions.canCopyCalculation && !permissions.canRemoveCalculation && !permissions.canEditCalculation;
        }

        canEditComponents() {
            return this.props.model.permission.value.canEdit || ProjectStore.isNew();
        }

        render() {
            let activeUser = this.getActiveUser();
            return (
                <div className="project-edit-page">
                    {(ProjectStore.duringCopy || ProjectStore.duringSave || ProjectStore.duringRevise) &&
                        <Loader fillContainer={true} showBalls={false} />}
                    <div className="page-body-titlebar" style={{ position: 'relative' }}>
                        <div className="flex-center-v">
                            <h3 className="page-body-title">{this.getTitle()}</h3>
                            <div className="page-body-title-separator"></div>
                            <div className="mass-export-dropdown">
                                <span>
                                    {this.renderDocumentationsDropdown()}
                                </span>
                            </div>

                            <div className="w-auto" />
                        </div>

                        <div className="page-body-action-buttons desktop-only">
                            {this.props.model.permission.value.canCopy &&
                                <Button processing={ProjectStore.duringCopy} theme={Button.Theme.BorderOnly}
                                    onClick={this.onCopyClick}>{__('Copy')}</Button>}
                            {this.props.model.permission.value.canDelete && <Button theme={Button.Theme.BorderOnly}
                                onClick={this.onDeleteClick}>{__('Delete')}</Button>}
                            {this.canEditComponents() &&
                                <Button className="button-save" processing={ProjectStore.duringSave}
                                    theme={Button.Theme.BorderOnly} icon={IconSave}
                                    disabled={!ProjectStore.canSave()}
                                    onClick={this.onSaveClick}>{__('Save')}</Button>}
                        </div>

                        <div className="project-edit-page-floaters mobile-only">
                            {/* <ProjectExpandableFloatingDropdown
                                canSave={ProjectStore.canSave}
                                canAddConfiguration={false}
                                canCopy={this.props.model.permission.value.canCopy}
                                canRevise={false}
                                canDelete={this.props.model.permission.value.canDelete}
                                transformOptions={[]}
                                documentationOptions={this.dropdownOptions}

                                onSaveClick={this.onSaveClick}
                                onDeleteClick={this.onDeleteClick}
                                onCopyClick={this.onCopyClick}
                                onReviseClick={undefined}

                                onDocumentationOptionClick={id => this.onDocsGenerateDropdownItemClick({ id: id })}
                                onTransformOptionClick={undefined}

                                onCreateNewConfigurationOptionClick={undefined}
                                onAddExistingConfigurationOptionClick={undefined}
                            /> */}
                        </div>
                    </div>

                    <div className="spaced-items-group-xs row row-cards-container rown-no-outer-gutter">
                        <div className="spaced-item column-6 column-xs-12">
                            <div className="multicard --left">
                                <TWSProjectGeneralInformation
                                    helpInfo={this.props.model.generalInformationHelpInfo.value}
                                    canEdit={this.canEditComponents()}
                                />
                                <ProjectCollaborationCard
                                    helpInfo={this.props.model.collaborationHelpInfo.value}
                                    canEditParticipants={this.canEditComponents()}
                                    canEditViewers={this.canEditComponents()}
                                    members={this.props.model.projectsMembers.members}
                                    projectMembers={this.getProjectMembers()}
                                    activeUser={activeUser}
                                    onParticipantsChanged={this.onCollaborationParticipantsChanged}
                                    onViewersChanged={this.onCollaborationViewersChanged}
                                />
                                <ProjectCustomer
                                    helpInfo={this.props.model.customerHelpInfo.value}
                                    canEdit={this.canEditComponents()}
                                    canAddExistingCustomer={this.canEditComponents()}
                                    twsCustomer={true}
                                />
                                <FilesCard
                                    id={this.props.model.id.value}
                                    setDirty={() => ProjectStore.setDirty(true)}
                                    helpInfo={this.props.model.filesHelpInfo.value}
                                    duringEdit={ProjectStore.filesDuringEdit}
                                    setDuringEdit={(value: any) => ProjectStore.setFilesDuringEdit(value)}
                                    initialFiles={this.props.model.files.items}
                                    initialItemsViewModel={this.props.model.files.itemsViewModel}
                                    allowEdit={this.canEditComponents()}
                                    onFilesChange={this.onProjectFilesChange}
                                    documentApiService={ProjectApiService}
                                />
                            </div>
                        </div>

                        <div className="spaced-item column-6 column-xs-12">
                            <div className="multicard --right">
                                <ComunicationCard helpInfo={this.props.model.communicationHelpInfo.value}
                                    projectId={this.props.model.id.value}
                                    projectNumber={this.props.model.generalInformation.number.value}
                                    appType={Message.ApplicationType.TWS}
                                    messageApiService={MessageAPIService}
                                    messagesApiService={MessagesAPIService}
                                />
                                {ProjectStore.formModel.id.value !== 0 && (
                                    <HistoryCard helpInfo={this.props.model.historyHelpInfo.value}
                                        projectId={ProjectStore.formModel.id.value}
                                        title={this.props.model.title}
                                        api={new TWSProjectAPIService()}
                                        projectEntityName='TWSProject' />)}
                            </div>

                        </div>
                    </div>


                    <ProjectDeleteModal
                        isOpen={ProjectStore.deleteModalVisible}
                        onDeleteClick={() => this.onDeletePostClick()}
                    />

                    <TWSProjectCalculations
                        canAddCalculation={this.props.model.permission.value.canAddCalculation}
                        isViewer={this.getIsViewer()}
                        canSetConfigurationOptional={false}
                    />
                </div>
            );
        }
    }
)

window.components.TWSProjectEditPage = TWSProjectEditPage;
